import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Sparkles, ChevronRight, BookOpenCheck } from "lucide-react";
import { useSideRail } from "@/state/SideRail";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const tabs: Record<string, { label: string; to: string }> = {
  parts: { label: "Parts Catalog", to: "/parts" },
  bom: { label: "BOM Explorer", to: "/bom" },
  changes: { label: "Changes", to: "/changes" },
  knowledge: { label: "Knowledge Base", to: "/knowledge" },
  copilot: { label: "Copilot", to: "/copilot" },
  integration: { label: "Teamcenter Integration", to: "/integration" },
  hub: { label: "Integrations Hub", to: "/hub" },
  audit: { label: "Actions Audit Log", to: "/audit" },
  insights: { label: "Saved Insights", to: "/insights" },
  settings: { label: "Settings", to: "/settings" },
  help: { label: "Help", to: "/help" },
};

const askable = new Set(["parts", "bom", "changes", "knowledge", "copilot"]);

export function TopBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { open, toggle } = useSideRail();
  const [guideOpen, setGuideOpen] = useState(false);

  const segments = pathname.split("/").filter(Boolean);
  const tabKey = segments[0] ?? "copilot";
  const tab = tabs[tabKey] ?? tabs.copilot;
  const subSegments = segments.slice(1);
  const showAsk = askable.has(tabKey);

  return (
    <header className="flex h-11 shrink-0 items-center justify-between border-b border-sidebar-border bg-sidebar px-4 text-sidebar-foreground">
      <nav className="flex min-w-0 items-center gap-1.5 text-[13px]">
        {subSegments.length === 0 ? (
          <span className="text-white">{tab.label}</span>
        ) : (
          <>
            <Link to={tab.to} className="text-sidebar-muted hover:text-white">
              {tab.label}
            </Link>
            {subSegments.map((s, i) => (
              <span key={i} className="flex items-center gap-1.5 min-w-0">
                <ChevronRight className="h-3 w-3 shrink-0 text-sidebar-muted/70" />
                <span
                  className={`truncate ${
                    i === subSegments.length - 1 ? "text-white" : "text-sidebar-muted"
                  } ${s.length > 20 ? "font-mono text-[12px]" : ""}`}
                  title={decodeURIComponent(s)}
                >
                  {decodeURIComponent(s)}
                </span>
              </span>
            ))}
          </>
        )}
      </nav>
      <div className="flex shrink-0 items-center gap-2">
        {showAsk && (
          <button
            onClick={() => toggle(`About the ${tab.label}`, defaultRailSuggestions(tabKey))}
            className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[12px] transition-colors ${
              open
                ? "border-primary bg-primary/20 text-white"
                : "border-sidebar-border bg-sidebar-hover text-sidebar-foreground hover:bg-sidebar-active hover:text-white"
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Ask Copilot
          </button>
        )}
        <kbd className="flex items-center gap-1 rounded-full border border-sidebar-border bg-sidebar-hover px-2 py-0.5 text-[11px] text-sidebar-muted">
          <span className="font-medium">⌘</span>
          <span>K</span>
        </kbd>
        <button onClick={() => setGuideOpen(true)} className="inline-flex items-center gap-1 text-[11.5px] text-sidebar-muted hover:text-white" title="Demo guide">
          <BookOpenCheck className="h-3.5 w-3.5" /> Demo guide
        </button>
        <Link to="/help" className="text-[12px] text-sidebar-muted hover:text-white">Help</Link>
      </div>
      <Sheet open={guideOpen} onOpenChange={setGuideOpen}>
        <SheetContent side="right" className="w-[420px] sm:max-w-none">
          <SheetHeader><SheetTitle className="text-[14px]">Demo guide — three hero flows</SheetTitle></SheetHeader>
          <ol className="mt-4 space-y-3 text-[12px]">
            <li className="rounded-sm border border-border bg-surface p-3">
              <div className="font-medium text-foreground">Flow 1 — ECN Impact Analysis</div>
              <div className="mt-1 text-muted-foreground">Open Copilot. Click the chip <span className="font-mono text-foreground">"What's the impact of ECN-2026-0142?"</span></div>
            </li>
            <li className="rounded-sm border border-border bg-surface p-3">
              <div className="font-medium text-foreground">Flow 2 — Engineering Reuse</div>
              <div className="mt-1 text-muted-foreground">Click the chip <span className="font-mono text-foreground">"Find a similar B-Pillar Reinforcement…"</span></div>
            </li>
            <li className="rounded-sm border border-border bg-surface p-3">
              <div className="font-medium text-foreground">Flow 3 — ECN Creation</div>
              <div className="mt-1 text-muted-foreground">Click the chip <span className="font-mono text-foreground">"Create an ECN to switch the supplier…"</span> Approve in the Action Preview.</div>
            </li>
          </ol>
          <div className="mt-5 rounded-sm border border-dashed border-border bg-canvas/40 p-3 text-[11px] text-muted-foreground">
            For demonstrator use. Reset session state from <span className="text-foreground">Settings → Demo controls</span> between runs.
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}

function defaultRailSuggestions(tabKey: string) {
  switch (tabKey) {
    case "parts":
      return [
        { id: "rs-p1", text: "Show all parts approaching mass target overruns" },
        { id: "rs-p2", text: "Find parts using DP980 across all programs" },
        { id: "rs-p3", text: "Which parts have stale CAE results?" },
      ];
    case "bom":
      return [
        { id: "rs-b1", text: "What changed between Front Door rev A and rev B?" },
        { id: "rs-b2", text: "Compare body-side outer mass across programs" },
      ];
    case "changes":
      return [
        { id: "rs-c1", text: "Which open ECNs need my approval?" },
        { id: "rs-c2", text: "Summarize the ECN-2026-0142 impact" },
      ];
    case "knowledge":
      return [
        { id: "rs-k1", text: "Find the right Ford steel spec for DP780 release" },
        { id: "rs-k2", text: "Show prior tariff-response playbooks" },
      ];
    default:
      return [
        { id: "s-4", text: "What's the latest LS-Dyna result on the front frame rail?" },
      ];
  }
}
